import type { CollectionBindingOverride } from '@vibesites/runtime';

/**
 * Curates a property photo gallery into a tour:
 * hero → arrival/exterior → community amenities → apartment experience.
 *
 * After the hero, arrival is monument (when the sign reads as landscaped
 * and maintained), then primary building exteriors, then at most three
 * drone views chosen for different perspectives. Resident amenities follow
 * in tour order — clubhouse, coworking, fitness, pool and outdoor living,
 * pets, recreation, packages — with the pool kept beside the clubhouse when
 * photos show they open onto each other. Apartment photos then lead with
 * virtually staged rooms in a walk through the home, and a handful of vacant
 * shots that add detail instead of repeating the model. Titles in this
 * portfolio are usually "Community" or "Apartment"; the subject lives in the
 * description and alt text. The pass is an edit, not a reorder of every
 * file: repetitive, outdated, and low-quality frames come out, drone and
 * vacant shots stay limited, and a strong photo leads its section instead
 * of sitting behind weaker frames just to hold a category slot. Photos we
 * cannot place stay at the end, and only when they still add something.
 */

export interface CuratableGalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  alt_text?: string | null;
  title?: string | null;
  description?: string | null;
}

export type GalleryTab = 'all' | 'community' | 'apartment';

export interface CuratedGallery<T> {
  all: T[];
  community: T[];
  apartment: T[];
}

type Stage =
  | 'monument'
  | 'exterior'
  | 'drone'
  | 'clubhouse'
  | 'coworking'
  | 'fitness'
  | 'pool'
  | 'grill'
  | 'pet'
  | 'recreation'
  | 'package'
  | 'otherAmenity'
  | 'lifestyle'
  | 'staged'
  | 'apartment'
  | 'vacant'
  | 'other';

type PhotoTab = 'community' | 'apartment' | null;

interface Classified<T> {
  photo: T;
  index: number;
  stage: Stage;
  room: number;
  tab: PhotoTab;
  text: string;
  captionKey: string;
}

const ROOM_DEFAULT = 80;
const MAX_DRONE = 3;
/** Vacant shots that follow a staged model: enough to show finishes, not a second tour. */
const MAX_VACANT_WITH_STAGED = 4;
const MAX_APARTMENT_WITHOUT_STAGED = 12;
const MAX_UNKNOWN_APARTMENT = 8;
const AMENITY_ANGLES = 2;

const STAGE_CAP: Partial<Record<Stage, number>> = {
  monument: 1,
  exterior: 3,
  drone: MAX_DRONE,
  clubhouse: 3,
  coworking: 2,
  fitness: 2,
  pool: 3,
  grill: 2,
  pet: 2,
  recreation: 2,
  package: 1,
  otherAmenity: 3,
  lifestyle: 2,
};

/**
 * Lower sorts earlier. Staged rooms are a block; unlabeled and vacant
 * apartment photos share a rank so the complement walk stays in room order.
 * Amenity ranks are replaced per property when related outdoor spaces or a
 * standout amenity should move.
 */
const STAGE_RANK: Record<Stage, number> = {
  monument: 10,
  exterior: 20,
  drone: 30,
  clubhouse: 40,
  coworking: 50,
  fitness: 60,
  pool: 70,
  grill: 80,
  pet: 90,
  recreation: 100,
  package: 110,
  otherAmenity: 120,
  lifestyle: 130,
  staged: 200,
  apartment: 210,
  vacant: 210,
  other: 300,
};

const AMENITY_STAGES: Stage[] = [
  'clubhouse',
  'coworking',
  'fitness',
  'pool',
  'grill',
  'pet',
  'recreation',
  'package',
  'otherAmenity',
  'lifestyle',
];

const DRONE = /\b(drones?|aerials?|bird'?s?\s*-?\s*eye)\b/;
const MONUMENT = /\b(monuments?|property signs?|entry signs?|community signs?|signage)\b/;
const EXTERIOR =
  /\b(exteriors?|façades?|facades?|elevations?|streetscapes?|arrivals?)\b|\bbuilding exterior\b|\b(building|community|property)\s+(?:view|exterior|front)s?\b|\bfront of (?:the )?(?:building|community|property)\b|\bbuilding views?\b/;
/** Signs that are temporary, damaged, or bare logo art — not an arrival shot. */
const MONUMENT_POOR =
  /\b(temp(?:orary)?\s+signs?|for[\s-]?lease signs?|construction signs?|banner signs?|unlandscaped|overgrown|dead (?:grass|landscaping|plants?)|weeds?|before landscap|damaged signs?|broken signs?|fallen signs?|logo close[\s-]?ups?|sign close[\s-]?ups?)\b/;
const MONUMENT_LANDSCAPED =
  /\b(landscap\w*|flowers?|gardens?|plantings?|manicured|well[\s-]?maintained|grounds|flower beds?|shrubs?|lawn)\b/;
const EXTERIOR_PRIMARY =
  /\b(primary|main|front|arrival|entry|entries|elevation|streetscape|porte[\s-]?cochere|building exterior|community exterior|property exterior|building views?)\b/;
const EXTERIOR_SECONDARY =
  /\b(dumpsters?|trash(?:\s+enclosures?)?|alleys?|loading docks?|back of house|mechanical|utility yards?|roof penetrations?|parking garage|garage interior)\b/;
const OUTDOOR_LINK =
  /\b(opens?\s+(?:on|onto|to)|overlooking|adjacent to|indoor[\s/-]*outdoor|pool deck|from the club(?:house|room)|off the club(?:house|room))\b/;
const DIFFERENTIATOR = /\b(signature|resort(?:\s|-)?style|showpiece|centerpiece|featured amenity)\b/;
const ANGLE_STRONG = /\b(hero|signature|model|primary|wide|overview|featured)\b|\b(twilight|dusk|sunset|golden hour)\b/;
const ANGLE_DETAIL = /\b(close[\s-]?ups?|details?|corners?|partial)\b/;
const VACANT_DETAIL =
  /\b(finishes|finish packages?|appliances?|islands?|peninsulas?|windows?|fireplaces?|ceiling fans?|fixtures?|layouts?)\b/;
const EDITORIAL_DROP =
  /\b(outdated|dated|before renovation|under construction|construction progress|blurry|out of focus|watermarks?|screenshot|placeholder image)\b/;
const DRONE_PERSPECTIVES: Array<[string, RegExp]> = [
  ['overhead', /\b(overhead|top[\s-]?down|straight[\s-]?down|bird'?s?\s*-?\s*eye|looking down)\b/],
  ['oblique', /\b(oblique|angled aerial|three[\s-]?quarter)\b/],
  ['entry', /\b(front|entry|arrival|monument)\b/],
  ['rear', /\b(rear|back of|behind)\b/],
  ['amenity', /\b(pool|clubhouse|amenity|courtyard|fitness|dog park)\b/],
  ['setting', /\b(neighborhood|setting|context|surroundings?|skyline|site)\b/],
  ['twilight', /\b(dusk|twilight|sunset|golden hour|night aerial)\b/],
];
const STAGED =
  /virtually\s+staged|virtual(?:ly)?\s+stag|virtually\s+furnish|\bstaged\b|\bfurnished\b|\bmodel\s+(?:apartments?|units?|homes?|kitchens?|livings?|bedrooms?|baths?)/;
const VACANT = /\bvacants?\b|\bunfurnished\b|\bempty\s+(?:apartments?|units?|kitchens?|rooms?|livings?)/;
const CLUBHOUSE = /\b(clubhouses?|club\s*rooms?|resident lounges?|resident clubs?|leasing offices?)\b|\blobb(?:y|ies)\b/;
const LOUNGE = /\blounges?\b/;
const COWORK = /\b(cowork(?:ing)?|co-working|business centers?|conference rooms?)\b/;
const FITNESS = /\b(fitness|gyms?|workouts?|exercise rooms?|yoga)\b/;
const POOL = /\b(pools?|swimming|hot tubs?|cabanas?|sundecks?|sun decks?)\b|\bspas?\b/;
const GRILL = /\b(grills?|bbqs?|barbecues?|barbeques?|outdoor kitchens?|fire\s*pits?|picnics?)\b/;
const PET = /\b(dog parks?|pet parks?|pet spas?|pet wash(?:es)?|bark parks?|dog wash(?:es)?)\b/;
const RECREATION =
  /\b(playgrounds?|basketball|tennis|volleyball|sports? courts?|game rooms?|billiards?|recreation|putting greens?|bocce)\b/;
const PACKAGE = /\b(packages?|parcels?|mail\s*rooms?)\b|\b(?:package|parcel)\s+lockers?\b/;
const APARTMENT_ROOM =
  /\b(kitchens?|bedrooms?|bathrooms?|baths?|living\s+(?:rooms?|spaces?|areas?)|dining\s+rooms?|islands?|peninsulas?|appliances?)\b/;
/** Logos / brand marks that should never appear as page heroes. */
const LOGO = /\blogos?\b|\bwordmarks?\b|\blogotypes?\b|\bbrand\s+marks?\b/;
/**
 * Unit floor-plan drawings / line art (not community site maps). These belong
 * on floorplan listings, not marketing heroes or lifestyle pickers.
 */
const FLOOR_PLAN_LAYOUT =
  /\bfloor\s*-?\s*plans?\b|\bfloorplans?\b|\bunit\s*-?\s*plans?\b|\bblueprints?\b|\bschemat(?:ic|ics)\b|\bline\s*arts?\b|\blayout\s+drawings?\b/;

const ROOM_PATTERNS: Array<{ rank: number; pattern: RegExp }> = [
  { rank: 10, pattern: /\b(entr(?:y|ance)|foyers?|entryways?)\b/ },
  { rank: 20, pattern: /\b(living\s+(?:rooms?|spaces?|areas?)|great\s+rooms?|family\s+rooms?)\b/ },
  { rank: 30, pattern: /\b(dining\s+rooms?)\b/ },
  { rank: 40, pattern: /\b(kitchens?|islands?|peninsulas?|appliances?)\b/ },
  { rank: 50, pattern: /\b(bedrooms?|primary\s+suites?|master\s+bedrooms?)\b/ },
  { rank: 60, pattern: /\b(bathrooms?|baths?|powder\s+rooms?)\b/ },
  {
    rank: 70,
    pattern:
      /\b(laundr(?:y|ies)|closets?|balcon(?:y|ies)|patios?|fireplaces?|windows?|ceiling\s+fans?|finishes|finish\s+packages?)\b/,
  },
];

export function galleryPhotoSrc(photo: CuratableGalleryPhoto): string {
  return photo.image || photo.original_url || '';
}

/** Editor reorder wins over the tour sequence. */
export function hasCustomGalleryOrder(
  override: CollectionBindingOverride | null | undefined,
): boolean {
  if (!override) return false;
  return override.orderedIds != null || (override.ordering?.length ?? 0) > 0;
}

function photoText(photo: CuratableGalleryPhoto): string {
  return [photo.title, photo.description, photo.alt_text]
    .map((value) => (value || '').toLowerCase())
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Caption plus URL filename tokens, for graphic filters that need file cues. */
function photoTextWithFilename(photo: CuratableGalleryPhoto): string {
  const urlName = [photo.image, photo.original_url]
    .map((value) => {
      if (!value) return '';
      const path = value.split('?')[0] ?? value;
      return path.split('/').pop() ?? path;
    })
    .join(' ');
  return [photoText(photo), urlName.toLowerCase().replace(/[/_+.-]+/g, ' ')]
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** True when caption/title text looks like a logo or brand mark. */
export function isLogoPhoto(photo: CuratableGalleryPhoto): boolean {
  return LOGO.test(photoTextWithFilename(photo));
}

/** True when caption/title text looks like a unit floor-plan drawing / line art. */
export function isFloorPlanLayoutPhoto(photo: CuratableGalleryPhoto): boolean {
  return FLOOR_PLAN_LAYOUT.test(photoTextWithFilename(photo));
}

/**
 * Logos and unit floor-plan layouts are not marketing photography — exclude
 * them from heroes and lifestyle pickers.
 */
export function isNonMarketingGraphic(photo: CuratableGalleryPhoto): boolean {
  return isLogoPhoto(photo) || isFloorPlanLayoutPhoto(photo);
}

/** Virtually staged / furnished / model-unit photography. */
export function isStagedPhoto(photo: CuratableGalleryPhoto): boolean {
  return STAGED.test(photoTextWithFilename(photo));
}

/** Logos, floor-plan art, and frames that read as outdated or low quality. */
function isEditoriallyDropped(photo: CuratableGalleryPhoto): boolean {
  if (isNonMarketingGraphic(photo)) return true;
  return EDITORIAL_DROP.test(photoTextWithFilename(photo));
}

function captionKey(photo: CuratableGalleryPhoto): string {
  const raw = (photo.description || photo.alt_text || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return raw.length >= 3 ? raw : '';
}

function roomRank(text: string): number {
  let best = ROOM_DEFAULT;
  for (const { rank, pattern } of ROOM_PATTERNS) {
    if (rank < best && pattern.test(text)) best = rank;
  }
  return best;
}

/** Keep a monument when it can read as a maintained entry. Drop bare or neglected signs. */
function monumentIsPresentable(text: string): boolean {
  if (MONUMENT_POOR.test(text)) return false;
  if (LOGO.test(text) && !MONUMENT_LANDSCAPED.test(text)) return false;
  return true;
}

function monumentPreference(text: string): number {
  return MONUMENT_LANDSCAPED.test(text) ? 0 : 1;
}

/** 0 = primary building view, 1 = other exterior, 2 = service/back-of-house. */
function exteriorPriority(text: string): number {
  if (EXTERIOR_SECONDARY.test(text)) return 2;
  if (EXTERIOR_PRIMARY.test(text)) return 0;
  return 1;
}

function dronePerspectiveKey<T>(item: Classified<T>): string {
  const hits = DRONE_PERSPECTIVES.filter(([, pattern]) => pattern.test(item.text)).map(([name]) => name);
  if (hits.length > 0) return hits.join('+');
  return item.captionKey || 'general';
}

/**
 * At most `max` aerials. When there are more, take different perspectives
 * (overhead vs entry vs setting, and different captions) before repeating one.
 */
function selectDiverseDrones<T>(drones: Classified<T>[], max: number, hero: Classified<T> | null): Classified<T>[] {
  if (max <= 0) return [];
  const ordered = [...drones].sort((a, b) => a.index - b.index);
  if (ordered.length <= max) return ordered;

  const heroKey = hero?.stage === 'drone' ? dronePerspectiveKey(hero) : null;
  const groups = new Map<string, Classified<T>[]>();
  for (const item of ordered) {
    const key = dronePerspectiveKey(item);
    const bucket = groups.get(key) ?? [];
    bucket.push(item);
    groups.set(key, bucket);
  }

  const keys = [...groups.keys()].sort((a, b) => Number(a === heroKey) - Number(b === heroKey));
  const picked: Classified<T>[] = [];
  let progress = true;
  while (picked.length < max && progress) {
    progress = false;
    for (const key of keys) {
      if (picked.length >= max) break;
      const next = groups.get(key)?.shift();
      if (!next) continue;
      picked.push(next);
      progress = true;
    }
  }
  return picked.sort((a, b) => a.index - b.index);
}

/**
 * After the hero: presentable monuments, primary exteriors, then a diverse
 * set of at most three drones. Preference order is what the stage cap keeps.
 */
function prepareArrival<T>(remainder: Classified<T>[], hero: Classified<T> | null): Classified<T>[] {
  const withoutPoorMonuments = remainder.filter(
    (item) => item.stage !== 'monument' || monumentIsPresentable(item.text),
  );
  const drones = withoutPoorMonuments.filter((item) => item.stage === 'drone');
  const droneSlots = MAX_DRONE - (hero?.stage === 'drone' ? 1 : 0);
  const keptDrones = new Set(selectDiverseDrones(drones, droneSlots, hero));

  const monuments = withoutPoorMonuments
    .filter((item) => item.stage === 'monument')
    .sort((a, b) => monumentPreference(a.text) - monumentPreference(b.text) || a.index - b.index);
  const exteriors = withoutPoorMonuments
    .filter((item) => item.stage === 'exterior')
    .sort((a, b) => exteriorPriority(a.text) - exteriorPriority(b.text) || a.index - b.index);
  const buildingViews = exteriors.filter((item) => exteriorPriority(item.text) < 2);
  // Service yards and dumpsters stay out of arrival when a real building view exists.
  const arrivalExteriors = buildingViews.length > 0 ? buildingViews : exteriors;
  const droneList = new Set(drones);
  const rest = withoutPoorMonuments.filter(
    (item) => item.stage !== 'monument' && item.stage !== 'exterior' && !droneList.has(item),
  );

  return [...monuments, ...arrivalExteriors, ...[...keptDrones].sort((a, b) => a.index - b.index), ...rest];
}

function communityStage(text: string, title: string): Stage {
  if (DRONE.test(text)) return 'drone';
  if (MONUMENT.test(text)) return 'monument';
  if (PET.test(text)) return 'pet';
  if (PACKAGE.test(text)) return 'package';
  if (POOL.test(text)) return 'pool';
  if (GRILL.test(text)) return 'grill';
  if (CLUBHOUSE.test(text) || (LOUNGE.test(text) && !POOL.test(text))) return 'clubhouse';
  if (COWORK.test(text)) return 'coworking';
  if (FITNESS.test(text)) return 'fitness';
  if (RECREATION.test(text)) return 'recreation';
  if (EXTERIOR.test(text)) return 'exterior';
  if (title.includes('lifestyle') || /\blifestyle\b/.test(text)) return 'lifestyle';
  return 'otherAmenity';
}

function looksLikeCommunity(text: string): boolean {
  return (
    DRONE.test(text) ||
    MONUMENT.test(text) ||
    EXTERIOR.test(text) ||
    PET.test(text) ||
    PACKAGE.test(text) ||
    POOL.test(text) ||
    GRILL.test(text) ||
    CLUBHOUSE.test(text) ||
    LOUNGE.test(text) ||
    COWORK.test(text) ||
    FITNESS.test(text) ||
    RECREATION.test(text) ||
    /\blifestyle\b/.test(text)
  );
}

function looksLikeApartment(text: string): boolean {
  return APARTMENT_ROOM.test(text) || STAGED.test(text) || VACANT.test(text);
}

function apartmentStage(text: string): Stage {
  if (STAGED.test(text)) return 'staged';
  if (VACANT.test(text)) return 'vacant';
  return 'apartment';
}

function classify<T extends CuratableGalleryPhoto>(photo: T, index: number): Classified<T> {
  const text = photoText(photo);
  const title = (photo.title || '').toLowerCase();
  const apartmentTitle = title.includes('apartment');
  const communityTitle = title.includes('community') || title.includes('lifestyle');
  const apartmentish = apartmentTitle || looksLikeApartment(text);
  const communityish = communityTitle || looksLikeCommunity(text);

  let stage: Stage;
  let tab: PhotoTab;
  if (apartmentTitle || (apartmentish && !communityish)) {
    stage = apartmentStage(text);
    tab = 'apartment';
  } else if (communityish) {
    stage = communityStage(text, title);
    tab = 'community';
  } else {
    stage = 'other';
    tab = null;
  }

  return {
    photo,
    index,
    stage,
    room: tab === 'apartment' ? roomRank(text) : 0,
    tab,
    text,
    captionKey: captionKey(photo),
  };
}

/**
 * Lead photo for the Property Photos hero and the gallery grid.
 * Prefer a first-impression community shot. The type changes with what the
 * property actually has: exterior, clubhouse / resident space, pool, then
 * another standout amenity. Apartment interiors, logos, and floor-plan
 * drawings never lead. Signs, package rooms, and drone shots only lead when
 * nothing stronger is labeled.
 */
function heroScore<T>(item: Classified<T>): number {
  if (item.tab === 'apartment') return 0;
  if (/\bhero\b/.test(item.text) && item.stage !== 'monument' && item.stage !== 'package') return 100;
  const twilight = /\b(twilight|dusk|sunset|golden hour)\b/.test(item.text);
  if (twilight && (item.stage === 'exterior' || item.stage === 'pool' || item.stage === 'clubhouse')) return 85;
  if (item.stage === 'exterior') return 70;
  if (item.stage === 'clubhouse') return 60;
  if (item.stage === 'pool') return 55;
  if (
    item.stage === 'fitness' ||
    item.stage === 'coworking' ||
    item.stage === 'grill' ||
    item.stage === 'recreation' ||
    item.stage === 'pet'
  ) {
    return 45;
  }
  if (item.stage === 'lifestyle') return 35;
  if (item.stage === 'otherAmenity') return 30;
  if (item.tab === 'community' && item.stage !== 'drone' && item.stage !== 'monument' && item.stage !== 'package') {
    return 20;
  }
  if (item.stage === 'drone') return 15;
  if (item.stage === 'monument') return monumentIsPresentable(item.text) ? 10 : 0;
  if (item.stage === 'package') return 5;
  return 0;
}

/** Strongest community photo to lead the page, or null when none qualifies. */
export function pickGalleryHeroPhoto<T extends CuratableGalleryPhoto>(photos: T[]): T | null {
  const classified = usablePhotos(photos)
    .filter((photo) => !isNonMarketingGraphic(photo))
    .map((photo, index) => classify(photo, index));

  let best: Classified<T> | null = null;
  let bestScore = 0;
  for (const item of classified) {
    const score = heroScore(item);
    if (score > bestScore) {
      best = item;
      bestScore = score;
    }
  }
  return best?.photo ?? null;
}

function pickHero<T>(items: Classified<T>[]): Classified<T> | null {
  let best: Classified<T> | null = null;
  let bestScore = 0;
  for (const item of items) {
    const score = heroScore(item);
    if (score > bestScore) {
      best = item;
      bestScore = score;
    }
  }
  if (best) return best;
  return items.find((item) => item.stage === 'staged') ?? items[0] ?? null;
}

function applyStageCaps<T>(items: Classified<T>[], hero: Classified<T> | null): Classified<T>[] {
  const counts = new Map<Stage, number>();
  if (hero && STAGE_CAP[hero.stage] != null) counts.set(hero.stage, 1);

  const kept: Classified<T>[] = [];
  for (const item of items) {
    const cap = STAGE_CAP[item.stage];
    if (cap == null) {
      kept.push(item);
      continue;
    }
    const used = counts.get(item.stage) ?? 0;
    if (used >= cap) continue;
    counts.set(item.stage, used + 1);
    kept.push(item);
  }
  return kept;
}

function angleStrength(text: string): number {
  if (ANGLE_DETAIL.test(text) && !ANGLE_STRONG.test(text)) return 3;
  if (ANGLE_STRONG.test(text)) return 0;
  return 1;
}

const AMENITY_STAGE_SET = new Set<Stage>(AMENITY_STAGES);

interface AmenityOrder {
  ranks: Record<Stage, number>;
}

function clubhouseMeetsOutdoor<T>(items: Classified<T>[]): boolean {
  return items.some((item) => {
    const mentionsClub = item.stage === 'clubhouse' || CLUBHOUSE.test(item.text) || LOUNGE.test(item.text);
    const mentionsOutdoor = item.stage === 'pool' || item.stage === 'grill' || POOL.test(item.text) || GRILL.test(item.text);
    if (mentionsClub && mentionsOutdoor) return true;
    return (
      (item.stage === 'clubhouse' || item.stage === 'pool' || item.stage === 'grill') &&
      OUTDOOR_LINK.test(item.text)
    );
  });
}

/** Tour order, with pool/grill parked beside the clubhouse when they connect, and a signature space moved forward. */
function buildAmenityOrder<T>(items: Classified<T>[]): AmenityOrder {
  const linkedOutdoor = clubhouseMeetsOutdoor(items);
  const clusters: Stage[][] = linkedOutdoor
    ? [
        ['clubhouse', 'pool', 'grill'],
        ['coworking'],
        ['fitness'],
        ['pet'],
        ['recreation'],
        ['package'],
        ['otherAmenity'],
        ['lifestyle'],
      ]
    : [
        ['clubhouse'],
        ['coworking'],
        ['fitness'],
        ['pool', 'grill'],
        ['pet'],
        ['recreation'],
        ['package'],
        ['otherAmenity'],
        ['lifestyle'],
      ];

  const standout = (stage: Stage) =>
    stage !== 'package' && items.some((item) => item.stage === stage && DIFFERENTIATOR.test(item.text));
  const hot = clusters.filter((cluster) => cluster.some(standout));
  const rest = clusters.filter((cluster) => !cluster.some(standout));
  const sequence = [...hot, ...rest].flat();

  const ranks = { ...STAGE_RANK };
  sequence.forEach((stage, index) => {
    ranks[stage] = 40 + index;
  });
  return { ranks };
}

/** Put the strongest angle of each amenity first so later caps keep that shot. */
function leadWithStrongAmenityAngles<T>(items: Classified<T>[]): Classified<T>[] {
  const queues = new Map<Stage, Classified<T>[]>();
  for (const stage of AMENITY_STAGES) queues.set(stage, []);
  for (const item of items) {
    if (AMENITY_STAGE_SET.has(item.stage)) queues.get(item.stage)!.push(item);
  }
  for (const stage of AMENITY_STAGES) {
    queues.get(stage)!.sort((a, b) => angleStrength(a.text) - angleStrength(b.text) || a.index - b.index);
  }
  const cursor = new Map<Stage, number>();
  return items.map((item) => {
    if (!AMENITY_STAGE_SET.has(item.stage)) return item;
    const queue = queues.get(item.stage)!;
    const index = cursor.get(item.stage) ?? 0;
    cursor.set(item.stage, index + 1);
    return queue[index]!;
  });
}

/** Keep the strongest shot and one complementary angle of each amenity, not every frame. */
function capAmenityAngles<T>(items: Classified<T>[]): Classified<T>[] {
  const counts = new Map<string, number>();
  const kept = new Set<Classified<T>>();
  for (const stage of AMENITY_STAGES) {
    const group = items
      .filter((item) => item.stage === stage)
      .sort((a, b) => angleStrength(a.text) - angleStrength(b.text) || a.index - b.index);
    for (const item of group) {
      if (!item.captionKey) {
        kept.add(item);
        continue;
      }
      const key = `${stage}:${item.captionKey}`;
      const used = counts.get(key) ?? 0;
      if (used >= AMENITY_ANGLES) continue;
      counts.set(key, used + 1);
      kept.add(item);
    }
  }
  return items.filter((item) => !AMENITY_STAGE_SET.has(item.stage) || kept.has(item));
}

function roomLimit(room: number, perRoom: number, unknownCap: number): number {
  return room === ROOM_DEFAULT ? unknownCap : perRoom;
}

function capPerRoom<T>(items: Classified<T>[], perRoom: number, unknownCap: number): Classified<T>[] {
  const counts = new Map<number, number>();
  return items.filter((item) => {
    const limit = roomLimit(item.room, perRoom, unknownCap);
    const used = counts.get(item.room) ?? 0;
    if (used >= limit) return false;
    counts.set(item.room, used + 1);
    return true;
  });
}

/** One of each room first, then extra angles up to the caps. */
function capApartmentSet<T>(
  items: Classified<T>[],
  maxTotal: number,
  perRoom: number,
  unknownCap: number,
): Classified<T>[] {
  const kept: Classified<T>[] = [];
  const perRoomCount = new Map<number, number>();
  const seen = new Set<Classified<T>>();

  const byRoom = [...items].sort(
    (a, b) => a.room - b.room || angleStrength(a.text) - angleStrength(b.text) || a.index - b.index,
  );
  for (const item of byRoom) {
    if (kept.length >= maxTotal) break;
    if ((perRoomCount.get(item.room) ?? 0) >= 1) continue;
    perRoomCount.set(item.room, 1);
    kept.push(item);
    seen.add(item);
  }

  for (const item of items) {
    if (kept.length >= maxTotal) break;
    if (seen.has(item)) continue;
    const limit = roomLimit(item.room, perRoom, unknownCap);
    const used = perRoomCount.get(item.room) ?? 0;
    if (used >= limit) continue;
    perRoomCount.set(item.room, used + 1);
    kept.push(item);
    seen.add(item);
  }

  return kept;
}

function stagedWalk<T>(item: Classified<T>): number {
  if (item.stage === 'staged' && item.room === ROOM_DEFAULT) return 0;
  return item.room;
}

function vacantAddsDetail<T>(item: Classified<T>, stagedRooms: Set<number>, stagedCaptions: Set<string>): boolean {
  if (item.captionKey && stagedCaptions.has(item.captionKey)) return false;
  const covered = item.room !== ROOM_DEFAULT && stagedRooms.has(item.room);
  if (covered && !VACANT_DETAIL.test(item.text)) return false;
  if (item.room === ROOM_DEFAULT && !VACANT_DETAIL.test(item.text)) return false;
  return true;
}

function limitApartments<T>(items: Classified<T>[]): Classified<T>[] {
  const nonApartment = items.filter((item) => item.tab !== 'apartment');
  const apartments = items.filter((item) => item.tab === 'apartment');
  const staged = apartments.filter((item) => item.stage === 'staged');
  const complement = apartments.filter((item) => item.stage !== 'staged');

  const stagedSorted = [...staged].sort(
    (a, b) => stagedWalk(a) - stagedWalk(b) || angleStrength(a.text) - angleStrength(b.text) || a.index - b.index,
  );
  const limitedStaged = capPerRoom(stagedSorted, 2, 4);

  if (limitedStaged.length === 0) {
    const sorted = [...complement].sort(
      (a, b) => a.room - b.room || angleStrength(a.text) - angleStrength(b.text) || a.index - b.index,
    );
    return [...nonApartment, ...capApartmentSet(sorted, MAX_APARTMENT_WITHOUT_STAGED, 2, MAX_UNKNOWN_APARTMENT)];
  }

  const stagedRooms = new Set(limitedStaged.map((item) => item.room).filter((room) => room !== ROOM_DEFAULT));
  const stagedCaptions = new Set(limitedStaged.map((item) => item.captionKey).filter((key) => key));
  const useful = complement
    .filter((item) => vacantAddsDetail(item, stagedRooms, stagedCaptions))
    .sort((a, b) => a.room - b.room || angleStrength(a.text) - angleStrength(b.text) || a.index - b.index);
  const limitedComplement = capApartmentSet(useful, MAX_VACANT_WITH_STAGED, 1, 2);
  return [...nonApartment, ...limitedStaged, ...limitedComplement];
}

function subjectKey<T>(item: Classified<T>): string {
  if (item.tab === 'apartment' && item.room !== ROOM_DEFAULT) return `apt:${item.stage}:${item.room}`;
  if (item.captionKey) return `cap:${item.stage}:${item.captionKey}`;
  return `solo:${item.index}`;
}

/** A repeat stays only when it shows a detail or a clearly stronger view than the shot already kept. */
function addsDistinctAngle<T>(kept: Classified<T>, next: Classified<T>): boolean {
  const nextDetail = ANGLE_DETAIL.test(next.text) || VACANT_DETAIL.test(next.text);
  const keptDetail = ANGLE_DETAIL.test(kept.text) || VACANT_DETAIL.test(kept.text);
  if (nextDetail && !keptDetail) return true;
  if (ANGLE_STRONG.test(next.text) && !ANGLE_STRONG.test(kept.text)) return true;
  return false;
}

function pruneRepetitiveAngles<T>(items: Classified<T>[]): Classified<T>[] {
  const groups = new Map<string, Classified<T>[]>();
  const order: string[] = [];
  for (const item of items) {
    const key = subjectKey(item);
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(item);
  }

  const kept = new Set<Classified<T>>();
  for (const key of order) {
    const group = groups.get(key)!.sort((a, b) => angleStrength(a.text) - angleStrength(b.text) || a.index - b.index);
    const [first, ...rest] = group;
    if (!first) continue;
    kept.add(first);
    for (const next of rest) {
      if (addsDistinctAngle(first, next)) kept.add(next);
    }
  }
  return items.filter((item) => kept.has(item));
}

function trimUnplaced<T>(items: Classified<T>[]): Classified<T>[] {
  const placed = items.some((item) => item.stage !== 'other');
  if (!placed) return items;
  const others = items
    .filter((item) => item.stage === 'other' && item.captionKey)
    .sort((a, b) => angleStrength(a.text) - angleStrength(b.text) || a.index - b.index)
    .slice(0, 2);
  const keep = new Set(others);
  return items.filter((item) => item.stage !== 'other' || keep.has(item));
}

/** Move one standout to the front of a section when weaker category frames would bury it. */
function promoteStrongLead<T>(items: Classified<T>[], inBand: (item: Classified<T>) => boolean): Classified<T>[] {
  const band = items.map((item, index) => ({ item, index })).filter(({ item }) => inBand(item));
  if (band.length < 2) return items;
  let best = band[0];
  for (const entry of band) {
    if (angleStrength(entry.item.text) < angleStrength(best.item.text)) best = entry;
  }
  if (best.index === band[0].index) return items;
  if (angleStrength(best.item.text) !== 0 || angleStrength(band[0].item.text) === 0) return items;
  const next = items.slice();
  const [moved] = next.splice(best.index, 1);
  const insertAt = next.findIndex((item) => inBand(item));
  next.splice(insertAt < 0 ? 0 : insertAt, 0, moved!);
  return next;
}

function sortCurated<T>(items: Classified<T>[], order: AmenityOrder): Classified<T>[] {
  return [...items].sort((a, b) => {
    const stageDiff = order.ranks[a.stage] - order.ranks[b.stage];
    if (stageDiff !== 0) return stageDiff;
    if (a.stage === 'monument' && b.stage === 'monument') {
      const pref = monumentPreference(a.text) - monumentPreference(b.text);
      if (pref !== 0) return pref;
    }
    if (a.stage === 'exterior' && b.stage === 'exterior') {
      const pref = exteriorPriority(a.text) - exteriorPriority(b.text);
      if (pref !== 0) return pref;
    }
    if (a.tab === 'apartment' && b.tab === 'apartment') {
      const walk = stagedWalk(a) - stagedWalk(b);
      if (walk !== 0) return walk;
      const strength = angleStrength(a.text) - angleStrength(b.text);
      if (strength !== 0) return strength;
    } else if (AMENITY_STAGE_SET.has(a.stage) && a.stage === b.stage) {
      const strength = angleStrength(a.text) - angleStrength(b.text);
      if (strength !== 0) return strength;
    }
    return a.index - b.index;
  });
}

function usablePhotos<T extends CuratableGalleryPhoto>(photos: T[]): T[] {
  return photos.filter((photo) => galleryPhotoSrc(photo));
}

/** Split an editor-ordered gallery into tabs without resequencing or dropping photos. */
export function groupGalleryByTab<T extends CuratableGalleryPhoto>(photos: T[]): CuratedGallery<T> {
  const usable = usablePhotos(photos);
  const community: T[] = [];
  const apartment: T[] = [];
  usable.forEach((photo, index) => {
    const item = classify(photo, index);
    if (item.tab === 'community') community.push(photo);
    else if (item.tab === 'apartment') apartment.push(photo);
  });
  return { all: usable, community, apartment };
}

export function curateGalleryPhotos<T extends CuratableGalleryPhoto>(photos: T[]): CuratedGallery<T> {
  const edited = usablePhotos(photos).filter((photo) => !isEditoriallyDropped(photo));
  const classified = edited.map((photo, index) => classify(photo, index));
  if (classified.length === 0) return { all: [], community: [], apartment: [] };

  const heroPhoto = pickGalleryHeroPhoto(edited);
  const hero =
    (heroPhoto && classified.find((item) => item.photo === heroPhoto)) ||
    pickHero(
      classified.filter(
        (item) => item.stage !== 'monument' || monumentIsPresentable(item.text),
      ),
    );
  const remainder = prepareArrival(
    classified.filter((item) => item !== hero),
    hero,
  );
  const capped = trimUnplaced(
    pruneRepetitiveAngles(
      limitApartments(capAmenityAngles(applyStageCaps(leadWithStrongAmenityAngles(remainder), hero))),
    ),
  );
  const flow = sortCurated(capped, buildAmenityOrder(classified));
  const ordered = promoteStrongLead(
    promoteStrongLead(
      promoteStrongLead(flow, (item) => item.stage === 'monument' || item.stage === 'exterior' || item.stage === 'drone'),
      (item) => AMENITY_STAGE_SET.has(item.stage),
    ),
    (item) => item.stage === 'staged',
  );

  const all = hero ? [hero, ...ordered] : ordered;
  const community = all.filter((item) => item.tab === 'community');
  const apartment = all.filter((item) => item.tab === 'apartment');

  return {
    all: all.map((item) => item.photo),
    community: community.map((item) => item.photo),
    apartment: apartment.map((item) => item.photo),
  };
}
